/**
 * Copyright © 2026 Adnuntius AS.
 */
import * as _ from 'lodash';

export interface NoteLink {
  id: string;
}

export interface NotesSaveTarget {
  id: string;
  notes?: NoteLink[];
  lineItemNotes?: NoteLink[];
  savePartial(param: string): Promise<any>;
  [key: string]: any;
}

interface NotesSaveTargetOptions {
  model: () => any;
  save: (payload: any, param: string) => Promise<any>;
  readOnly?: () => boolean;
  onSaved?: (param: string, links: NoteLink[], saved: any) => void;
}

const NOTE_LINK_FIELDS = ['notes', 'lineItemNotes'];

export function snapshotNoteLinks(source: any): Record<string, NoteLink[]> {
  const snapshot: Record<string, NoteLink[]> = {};
  NOTE_LINK_FIELDS.forEach((field) => {
    if (Array.isArray(source?.[field])) {
      snapshot[field] = _.cloneDeep(source[field]);
    }
  });
  return snapshot;
}

export function syncNoteLinks(source: any, target: any): void {
  if (!target) {
    return;
  }
  NOTE_LINK_FIELDS.forEach((field) => {
    if (Array.isArray(source?.[field])) {
      target[field] = _.cloneDeep(source[field]);
    }
  });
}

export function createNotesSaveTarget(
  options: NotesSaveTargetOptions,
  param = 'notes'
): NotesSaveTarget {
  const model = options.model() || {};
  const target = {
    id: model.id,
    savePartial(this: NotesSaveTarget, saveParam: string) {
      if (options.readOnly?.()) {
        return Promise.reject('Read-only is the sole option');
      }
      const links = _.cloneDeep(this[saveParam] || []);
      const payload = {id: this.id, [saveParam]: links};
      return options.save(payload, saveParam).then((saved) => {
        options.onSaved?.(saveParam, links, saved);
        return saved;
      });
    }
  } as NotesSaveTarget;
  syncNoteLinks(model, target);
  target[param] = target[param] || [];
  return target;
}
